import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Card } from './common/Card';

// Default sector dependency rules (fallback if not loaded from settings)
const DEFAULT_SECTOR_DEPS = {
  energy: [], telecommunications: ['energy'], water: ['energy'],
  financial: ['energy', 'telecommunications'], banking: ['energy', 'telecommunications'],
  health: ['energy', 'telecommunications', 'water'], transportation: ['energy', 'telecommunications'],
  government: ['energy', 'telecommunications'], manufacturing: ['energy', 'telecommunications', 'water'],
  food_agriculture: ['energy', 'telecommunications', 'water'], chemical: ['energy', 'telecommunications', 'water'],
  defense: ['energy', 'telecommunications', 'government'], information_technology: ['energy', 'telecommunications'],
};

const SECTOR_COLORS = {
  energy: '#eab308',
  telecommunications: '#3b82f6',
  water: '#06b6d4',
  financial: '#22c55e',
  banking: '#22c55e',
  health: '#ec4899',
  transportation: '#f97316',
  government: '#8b5cf6',
  manufacturing: '#64748b',
  food_agriculture: '#84cc16',
  chemical: '#a855f7',
  defense: '#1e3a5f',
  information_technology: '#0ea5e9',
};

const CRITICALITY_SIZE = { MA: 22, A: 18, M: 14, B: 10, MB: 7 };

function normalizeSectorKey(sector) {
  if (!sector || sector === '—' || sector === 'Unclassified') return null;
  return sector.toLowerCase().replace(/[\s/-]+/g, '_').replace(/[()]/g, '');
}

export default function DependencyMap({ data, sectorDeps }) {
  const SECTOR_DEPS = sectorDeps || DEFAULT_SECTOR_DEPS;
  const fgRef = useRef();
  const containerRef = useRef();
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });

  // Responsive sizing
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: 500 });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Zoom to fit after graph stabilizes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (fgRef.current) fgRef.current.zoomToFit(400, 40);
    }, 1500);
    return () => clearTimeout(timer);
  }, [data]);

  // Pre-load logo images for canvas rendering
  const [logoImages, setLogoImages] = useState({});
  useEffect(() => {
    const rows = data?.oes_criticality_rows || [];
    const toLoad = rows.filter(r => r.logo && CRITICALITY_SIZE[r.criticality?.toUpperCase()] >= 14);
    if (toLoad.length === 0) return;
    const loaded = {};
    let remaining = toLoad.length;
    toLoad.forEach(r => {
      const img = new Image();
      img.onload = () => {
        loaded[r.oes_id] = img;
        remaining--;
        if (remaining === 0) setLogoImages({ ...loaded });
      };
      img.onerror = () => {
        remaining--;
        if (remaining === 0) setLogoImages({ ...loaded });
      };
      img.src = r.logo.startsWith('data:') ? r.logo : `data:image/png;base64,${r.logo}`;
    });
  }, [data]);

  // Build graph data
  const graphData = useMemo(() => {
    const rows = data?.oes_criticality_rows || [];
    if (rows.length === 0) return { nodes: [], links: [] };

    const nodes = rows.map(r => {
      const sk = normalizeSectorKey(r.sector);
      return {
        id: r.oes_id,
        name: r.oes_name || `OES ${r.oes_id}`,
        sector: r.sector || 'Unclassified',
        sectorKey: sk,
        criticality: r.criticality?.toUpperCase() || 'M',
        interdependency: r.interdependencia_score || 1,
        val: CRITICALITY_SIZE[r.criticality?.toUpperCase()] || 12,
        color: SECTOR_COLORS[sk] || '#94a3b8',
        hasLogo: !!(r.logo && CRITICALITY_SIZE[r.criticality?.toUpperCase()] >= 14),
      };
    });

    // Build directional links: source = dependent, target = provider (arrow points to provider)
    const links = [];
    const seen = new Set();
    for (let i = 0; i < nodes.length; i++) {
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue;
        const dep = nodes[i], provider = nodes[j];
        if (!dep.sectorKey || !provider.sectorKey) continue;
        const deps = SECTOR_DEPS[dep.sectorKey] || [];
        if (deps.includes(provider.sectorKey)) {
          const key = `${dep.id}->${provider.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const weight = ((dep.interdependency + provider.interdependency) / 2) / 5;
          links.push({ source: dep.id, target: provider.id, weight });
        }
      }
    }

    return { nodes, links };
  }, [data]);

  // Configure forces: stronger repulsion so nodes spread out with many OESes
  useEffect(() => {
    if (fgRef.current) {
      fgRef.current.d3Force('charge').strength(-300);
      fgRef.current.d3Force('link').distance(100);
    }
  }, [graphData]);

  // Connected node IDs for hover highlighting
  const connectedTo = useMemo(() => {
    if (hovered == null) return new Set();
    const ids = new Set([hovered]);
    graphData.links.forEach(l => {
      const sid = typeof l.source === 'object' ? l.source.id : l.source;
      const tid = typeof l.target === 'object' ? l.target.id : l.target;
      if (sid === hovered) ids.add(tid);
      if (tid === hovered) ids.add(sid);
    });
    return ids;
  }, [hovered, graphData.links]);

  // Node rendering
  const paintNode = useCallback((node, ctx, globalScale) => {
    const r = Math.sqrt(node.val) * 1.8;
    const isHighlighted = hovered == null || connectedTo.has(node.id);
    const isSelected = selected === node.id;
    const alpha = isHighlighted ? 1 : 0.15;
    const logoImg = node.hasLogo ? logoImages[node.id] : null;

    // Shadow for selected
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
      ctx.fill();
    }

    // Colored ring (sector color as border accent)
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    const ringAlpha = alpha < 1 ? alpha : (isSelected ? 1 : 0.7);
    ctx.strokeStyle = alpha < 1
      ? `${node.color}${Math.round(ringAlpha * 255).toString(16).padStart(2, '0')}`
      : node.color;
    ctx.lineWidth = isSelected ? 3 : 2.5;
    ctx.stroke();

    // White fill
    ctx.beginPath();
    ctx.arc(node.x, node.y, r - 1.5, 0, 2 * Math.PI);
    ctx.fillStyle = alpha < 1 ? `rgba(255,255,255,${alpha * 0.6})` : '#ffffff';
    ctx.fill();

    // Logo inside node (only for M/A/MA criticality with available logo)
    if (logoImg && alpha > 0.5) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(node.x, node.y, r - 3, 0, 2 * Math.PI);
      ctx.clip();
      // Fit logo preserving aspect ratio (contain, not stretch)
      const innerR = (r - 3) * 0.7;
      const aspect = logoImg.width / logoImg.height;
      let drawW, drawH;
      if (aspect >= 1) {
        drawW = innerR * 2;
        drawH = drawW / aspect;
      } else {
        drawH = innerR * 2;
        drawW = drawH * aspect;
      }
      ctx.drawImage(logoImg, node.x - drawW / 2, node.y - drawH / 2, drawW, drawH);
      ctx.restore();
    }

    // Label (always show if zoomed in enough, or if highlighted)
    if (globalScale > 1.2 || isSelected || (hovered != null && connectedTo.has(node.id))) {
      const fontSize = Math.max(10 / globalScale, 3);
      ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = alpha < 1 ? `rgba(30,41,59,${alpha})` : '#1e293b';
      ctx.fillText(node.name, node.x, node.y + r + 2);
    }
  }, [hovered, selected, connectedTo, logoImages]);

  // Link rendering
  const paintLink = useCallback((link, ctx) => {
    const sid = typeof link.source === 'object' ? link.source.id : link.source;
    const tid = typeof link.target === 'object' ? link.target.id : link.target;
    const isHighlighted = hovered == null || connectedTo.has(sid) && connectedTo.has(tid);
    const alpha = isHighlighted ? 0.4 + link.weight * 0.4 : 0.04;

    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.strokeStyle = `rgba(148, 163, 184, ${alpha})`;
    ctx.lineWidth = link.weight * 1.2 + 0.6;
    ctx.stroke();
  }, [hovered, connectedTo]);

  // Selected node info
  const selectedNode = selected ? graphData.nodes.find(n => n.id === selected) : null;
  const selectedConnections = selected ? graphData.links
    .filter(l => {
      const sid = typeof l.source === 'object' ? l.source.id : l.source;
      const tid = typeof l.target === 'object' ? l.target.id : l.target;
      return sid === selected || tid === selected;
    })
    .map(l => {
      const sid = typeof l.source === 'object' ? l.source.id : l.source;
      const tid = typeof l.target === 'object' ? l.target.id : l.target;
      const otherId = sid === selected ? tid : sid;
      const other = graphData.nodes.find(n => n.id === otherId);
      return other;
    })
    .filter(Boolean) : [];

  // Sector legend
  const activeSectors = useMemo(() => {
    const seen = new Map();
    graphData.nodes.forEach(n => {
      if (n.sectorKey && !seen.has(n.sectorKey)) {
        seen.set(n.sectorKey, { key: n.sectorKey, label: n.sector, color: n.color });
      }
    });
    return Array.from(seen.values());
  }, [graphData.nodes]);

  const CRIT_LABELS = { MA: 'Very High', A: 'High', M: 'Moderate', B: 'Low', MB: 'Very Low' };
  const CRIT_BADGE = {
    MA: 'bg-red-50 text-red-700 ring-red-100',
    A: 'bg-orange-50 text-orange-700 ring-orange-100',
    M: 'bg-amber-50 text-amber-700 ring-amber-100',
    B: 'bg-blue-50 text-blue-700 ring-blue-100',
    MB: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  };

  if (graphData.nodes.length === 0) {
    return (
      <Card className="p-8 text-center text-slate-400 text-sm">
        No OES criticality data available to build the dependency map.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800">OES Dependency Network</h3>
          <p className="text-xs text-slate-400 mt-0.5">Arrows point from dependent to provider. Node size = criticality level.</p>
        </div>
        <button
          onClick={() => fgRef.current?.zoomToFit(400, 40)}
          className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
        >
          Fit to view
        </button>
      </div>

      <Card className="p-0 overflow-hidden" ref={containerRef}>
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          width={dimensions.width}
          height={dimensions.height}
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            const r = Math.sqrt(node.val) * 1.8 + 4;
            ctx.beginPath();
            ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          linkCanvasObject={paintLink}
          onNodeHover={node => setHovered(node?.id ?? null)}
          onNodeClick={node => setSelected(prev => prev === node?.id ? null : node?.id)}
          onNodeDragEnd={node => { node.fx = node.x; node.fy = node.y; }}
          onBackgroundClick={() => setSelected(null)}
          linkDirectionalArrowLength={6}
          linkDirectionalArrowRelPos={0.85}
          linkDirectionalArrowColor={() => 'rgba(100, 116, 139, 0.5)'}
          d3AlphaDecay={0.025}
          d3VelocityDecay={0.3}
          cooldownTicks={150}
          backgroundColor="transparent"
        />
      </Card>

      {/* Legend + Detail panel */}
      <div className="flex flex-wrap gap-6">
        {/* Sector legend */}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Sectors</p>
          <div className="flex flex-wrap gap-2">
            {activeSectors.map(s => (
              <div key={s.key} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-xs text-slate-600">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Detail panel (when node selected) */}
        {selectedNode && (
          <Card className="p-4 w-72 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: selectedNode.color }} />
              <span className="text-sm font-bold text-slate-800 truncate">{selectedNode.name}</span>
            </div>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Sector</span>
                <span className="font-medium text-slate-700">{selectedNode.sector}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Criticality</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${CRIT_BADGE[selectedNode.criticality] || ''}`}>
                  {CRIT_LABELS[selectedNode.criticality] || selectedNode.criticality}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Interdependency</span>
                <span className="font-medium text-slate-700">{selectedNode.interdependency}/5</span>
              </div>
              {selectedConnections.length > 0 && (
                <div className="pt-2 mt-2 border-t border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Connected to ({selectedConnections.length})</p>
                  <div className="space-y-1">
                    {selectedConnections.map(c => (
                      <div key={c.id} className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                        <span className="truncate">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
